import * as React from 'react';
import WaveSurfer from 'wavesurfer.js';
import { RegionInfo, WaveformData } from '../models';
import { Button } from 'antd';
import './Waveform.css';

import CursorPlugin from 'wavesurfer.js/src/plugin/cursor';
import RegionsPlugin, { Region } from 'wavesurfer.js/src/plugin/regions';
import { CheckOutlined, CloseOutlined, ReloadOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { handleWaveformScroll } from './waveform-utils';

type WaveformProps = {
  children?: React.ReactNode;
  duration?: number;
  playerRef: React.RefObject<HTMLMediaElement>;
  onClose: () => void;
  onSubmit: (start: number, end: number) => void;
  regions?: RegionInfo[];
  waveformData?: WaveformData;
};

interface WaveformState {
  regions?: RegionInfo[];
  start?: number;
  end?: number;
  initialProgress?: number;
  initialZoom: number;
  minZoom: number;
  maxZoom: number;
  overlapAllowed: boolean;
  zoom: number;
  shouldScroll: boolean;
}

class Waveform extends React.Component<WaveformProps, WaveformState> {
  private waveformRef: React.RefObject<HTMLDivElement>;

  private wavesurfer!: WaveSurfer;
  constructor(props: WaveformProps) {
    super(props);

    this.state = {
      initialZoom: 20,
      minZoom: 10,
      maxZoom: 250,
      overlapAllowed: false,
      zoom: 20,
      shouldScroll: false,
    };

    this.waveformRef = React.createRef<HTMLDivElement>();
  }

  componentDidMount = () => {
    if (this.waveformRef.current) {
      console.debug('creating wavesurfer');
      this.wavesurfer = WaveSurfer.create({
        container: this.waveformRef.current,
        backend: 'MediaElement',
        removeMediaElementOnDestroy: false,
        scrollParent: true,
        hideScrollbar: true,
        autoCenter: true,
        autoCenterImmediately: true,
        maxCanvasWidth: 10000,
        mediaControls: true,
        progressColor: '#0065b3',
        minPxPerSec: 15,
        partialRender: true,
        barWidth: 4,
        barMinHeight: 0.5,
        plugins: [
          CursorPlugin.create({
            showTime: true,
            followCursorY: 'true',
          }),
          RegionsPlugin.create({
            name: 'regions',
            dragSelection: false,
          }),
        ],
      });
      this.wavesurfer.on('region-updated', this.regionUpdated);
      this.wavesurfer.on('region-click', this.regionClicked);
      this.waveformRef.current.addEventListener('wheel', this.onScroll, { passive: false });
    }
  };

  regionUpdated = (region: any) => {
    if (!this.state.regions) {
      return;
    }
    const { overlapAllowed, regions } = this.state;
    const { start, end } = region;

    if (regions.length > 1 && !overlapAllowed) {
      if (regions.length === 3) {
        if (start < regions[0].end) {
          region.start = regions[0].end;
        } else if (end > regions[2].start) {
          region.end = regions[2].start;
        }
      } else {
        const otherRegion = regions.find(({ resize }) => !resize);
        const currentRegion = regions.find(({ resize }) => !!resize);
        if (otherRegion && currentRegion) {
          if (otherRegion.end <= currentRegion.start) {
            if (start < otherRegion.end) {
              region.start = otherRegion.end;
            }
          } else if (otherRegion.start >= currentRegion.end) {
            if (end > otherRegion.start) {
              region.end = otherRegion.start;
            }
          }
        }
      }
      this.setState({ start: region.start, end: region.end });
    }
  };

  regionClicked = (region: Region, e: PointerEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.stopPropagation(); // prevent triggering a click on the waveform
      region.play(0);
    }
  };

  componentDidUpdate = (prevProps: WaveformProps) => {
    if (prevProps.waveformData !== this.props.waveformData) {
      if (this.props.waveformData) {
        this.loadWavesurfer(this.props.waveformData);
      }
    }

    if (prevProps.regions !== this.props.regions) {
      if (this.props.regions) {
        this.calculateZoom(this.props.regions);
        this.addRegions(this.props.regions);
      } else {
        this.onHide();
      }
    }
  };

  addRegions = async (regions: RegionInfo[]) => {
    this.wavesurfer.clearRegions();
    let initialProgress;
    regions.forEach(({ start, end, color, handleStyle, loop, resize }) => {
      this.wavesurfer.addRegion({
        end,
        start,
        color,
        handleStyle,
        resize: resize || false,
        drag: false,
      });

      if (resize && this.props.duration && this.props.duration !== 0) {
        if (start > end) {
          this.setState({ overlapAllowed: true });
        }
        const newEnd = Math.max(start, end);
        const newStart = Math.min(start, end);
        const time = newStart + (newEnd - newStart) / 2;
        const progress = time / this.props.duration;
        initialProgress = progress;
        this.wavesurfer.seekAndCenter(progress);
      } else {
        initialProgress = 0;
      }
    });

    this.setState({
      initialProgress,
      regions,
    });
  };

  onHide = () => {
    this.wavesurfer.clearRegions();
    this.setState({
      regions: undefined,
      start: undefined,
      end: undefined,
      initialProgress: undefined,
    });
  };

  componentWillUnmount = () => {
    if (this.waveformRef.current) {
      this.waveformRef.current.removeEventListener('wheel', this.onScroll);
    }
    try {
      this.wavesurfer.destroy();
    } catch (e) {
      // Ignore errors when destroying wavesurfer
      // TODO(rkjaran): This is not enough since an unfinished Ajax request
      //                is not properly cancelled (on Firefox).
    }
  };

  onScroll = (event: WheelEvent) => {
    handleWaveformScroll(this.wavesurfer, this.state.zoom, (newZoom) => this.setState({ zoom: newZoom }), event);
  };

  loadWavesurfer = ({ data }: WaveformData) => {
    const { playerRef } = this.props;

    if (playerRef.current) {
      playerRef.current.preload = 'none';
      this.wavesurfer.load(playerRef.current, data);
      this.wavesurfer.zoom(20);
    }
  };

  calculateZoom = (regions: RegionInfo[]) => {
    const activeRegion = regions.find(({ resize }) => !!resize);
    if (!activeRegion) {
      return;
    }

    if (this.waveformRef.current) {
      const duration = activeRegion.end - activeRegion.start;
      const width = this.waveformRef.current.clientWidth;

      const zoom = Math.min((width * 0.75) / duration, this.state.maxZoom);
      this.wavesurfer.zoom(zoom);
      this.setState({
        initialZoom: zoom,
        zoom,
      });
    }
  };

  resetZoom = () => {
    this.wavesurfer.zoom(this.state.initialZoom);
    if (this.state.initialProgress) {
      this.wavesurfer.seekAndCenter(this.state.initialProgress);
    }
    this.setState((prevState) => ({
      zoom: prevState.initialZoom,
    }));
  };

  onZoom = (out: boolean) => {
    this.setState((prevState) => {
      let newZoom = out ? prevState.zoom * 0.8 : prevState.zoom * 1.2;
      if (newZoom <= prevState.minZoom) {
        newZoom = prevState.minZoom;
      } else if (newZoom >= prevState.maxZoom) {
        newZoom = prevState.maxZoom;
      }
      this.wavesurfer.zoom(newZoom);
      return {
        ...prevState,
        zoom: newZoom,
      };
    });
  };

  onSubmit = () => {
    const { start, end } = this.state;
    if (!start || !end) {
      this.props.onClose();
    } else {
      this.props.onSubmit(start, end);
    }
  };

  render() {
    const { onClose, regions } = this.props;
    return (
      <div className={`waveform-container ${!regions ? 'hidden' : ''}`}>
        <div ref={this.waveformRef} className='waveform' />
        <div className={'waveform-buttons'}>
          <Button
            icon={<ZoomOutOutlined />}
            disabled={this.state.minZoom === this.state.zoom}
            onClick={() => this.onZoom(true)}
          >
            Þysja út
          </Button>
          <Button icon={<ReloadOutlined />} onClick={this.resetZoom} />
          <Button
            icon={<ZoomInOutlined />}
            disabled={this.state.maxZoom === this.state.zoom}
            onClick={() => this.onZoom(false)}
          >
            Þysja inn
          </Button>
          <Button icon={<CloseOutlined />} onClick={onClose}>
            Loka
          </Button>
          <Button icon={<CheckOutlined />} onClick={this.onSubmit}>
            Breyta
          </Button>
        </div>
      </div>
    );
  }
}

export default React.memo(Waveform);
